import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import BasePage from "../components/BasePage";
import Hero from "../components/Hero";
import BaseL2 from "./stackDoc/BaseL2";
import SafeERC20 from "./stackDoc/SafeERC20";
import BurnMintMechanism from "./stackDoc/BurnMintMechanism";
import FairBurn from "./stackDoc/FairBurn";
import SupplyControl from "./stackDoc/SupplyControl";
import { motion } from "framer-motion";
import { ArrowUp } from "lucide-react";

import imgR from "../assets/img/img-hero-r.webp";
import imgL from "../assets/img/img-hero-l.webp";

const sections = [
  { id: "base-l2", label: "Base L2", component: <BaseL2 /> },
  { id: "safe-erc20", label: "SafeERC20", component: <SafeERC20 /> },
  { id: "burn-mint", label: "Burn & Mint", component: <BurnMintMechanism /> },
  { id: "fair-burn", label: "Fair Burn", component: <FairBurn /> },
  {
    id: "supply-control",
    label: "Supply Control",
    component: <SupplyControl />,
  },
];

const StackPage: React.FC = () => {
  const [showNav, setShowNav] = useState(false);
  const [activeSection, setActiveSection] = useState<string>(sections[0].id);
  const [showTopBtn, setShowTopBtn] = useState(false);
  const { hash } = useLocation();

  // When the URL hash changes, scroll smoothly to the matching section
  useEffect(() => {
    if (hash) {
      const targetId = hash.replace("#", "");
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [hash]);

  // On window scroll, toggle the fixed nav & back-to-top button,
  // and update which section is currently “active” (highlighted)
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setShowNav(scrollY > 200);
      setShowTopBtn(scrollY > 200);

      const offset = 100;
      let current = sections[0].id;
      sections.forEach((sec) => {
        const el = document.getElementById(sec.id);
        if (el && el.offsetTop <= scrollY + offset) {
          current = sec.id;
        }
      });
      setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // initial check on mount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <BasePage
      title="Project Stack – UcWaves"
      description="Explore UcWaves' secure and decentralized smart contract stack, including mint/burn mechanics, SafeERC20, and supply control."
    >
      <Hero />

      {/* Fixed navigation bar (appears after scrolling past 200px) */}
      {showNav && (
        <nav className="fixed inset-x-0 top-20 z-50 w-full bg-white shadow-md py-4 px-6">
          <div className="flex flex-wrap justify-center gap-6 text-sm font-semibold">
            {sections.map((sec) => (
              <a
                key={sec.id}
                href={`#${sec.id}`}
                onClick={() => setActiveSection(sec.id)}
                className={`transition px-2 py-1 rounded ${
                  activeSection === sec.id
                    ? "text-[var(--color-secondary-dark)] bg-[var(--color-background)]"
                    : "text-[var(--color-primary-dark)] hover:text-[var(--color-secondary-dark)]"
                }`}
              >
                {sec.label}
              </a>
            ))}
          </div>
        </nav>
      )}

      {/* Main content */}
      <main>
        <section className="relative py-10 px-4 sm:px-6 lg:px-8 max-w-screen-2xl mx-auto text-justify">
          <img
            src={imgL}
            alt=""
            aria-hidden="true"
            className="hidden md:block absolute left-4 md:left-16 xl:left-32  top-5 transform -translate-y-5  w-24 md:w-40 xl:w-64 animate-fade-in-up object-contain z-10"
          />
          {/* Decorative image right */}
          <img
            src={imgR}
            alt=""
            aria-hidden="true"
            className="hidden md:block absolute right-4 md:right-16 xl:right-32 top-5 transform -translate-y-5 w-24 md:w-40 xl:w-64 animate-fade-in-up object-contain z-10"
          />

          {/* Content wrapper to ensure above images */}
          <div className="relative z-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                ease: "easeOut",
                type: "spring",
                stiffness: 200,
                damping: 20,
              }}
              viewport={{ once: true }}
              className="flex items-center justify-center"
            >
              <h1 className="text-6xl sm:text-6xl md:text-text-[64px]  md:mt-15  font-bold text-secondary-light text-center shadow-red-700 mb-32 ">
                UnCensored Waves <br />
                Stack Overview
              </h1>
            </motion.div>
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              ease: "easeOut",
              type: "spring",
              stiffness: 200,
              damping: 20,
            }}
            viewport={{ once: true }}
            className="text-center text-primary-dark text-2xl sm:text-5xl font-bold mb-10"
          >
            Why We Are Building on Base L2
          </motion.h2>
        </section>

        <div className="max-w-6xl mx-auto space-y-20">
          {sections.map((sec) => (
            <section key={sec.id} id={sec.id} className="scroll-mt-32">
              <div>{sec.component}</div>
            </section>
          ))}
        </div>
      </main>

      {/* Back to Top button */}
      {showTopBtn && (
        <motion.button
          onClick={scrollToTop}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-6 right-6 p-3 rounded-full bg-[var(--color-primary-dark)] text-white shadow-lg hover:bg-[var(--color-secondary-dark)] focus:outline-none text-justify"
        >
          <ArrowUp size={24} />
        </motion.button>
      )}
    </BasePage>
  );
};

export default StackPage;
